import { useState } from "react";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useSubscription } from "lib/hooks/subscription";
import { useRestricedViewNotice } from "lib/hooks/useRestrictedRowClick";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { usePaywall } from "lib/providers/Paywall";
import { routes, settingsRoutes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { BidReportType } from "lib/types/graphQLEnums";
import AskStotlesAiModal from "../requirement_summary_report_section/ask_question_modal/AskStotlesAiModal";

type Props = {
  title: string;
  noticeId: string | undefined;
  noticeTitle: string | undefined;
  buyerName: string | undefined;
  buyerId: string | undefined;
  reportId: string;
  bidId: string;
  bidName: string | undefined;
  showAskAI?: boolean;
  hasEditAccess: boolean;
  reportType: BidReportType;
};

export function ReportNoticeDetails({
  title,
  noticeId,
  noticeTitle,
  buyerName,
  buyerId,
  reportId,
  bidId,
  bidName,
  showAskAI = true,
  hasEditAccess,
  reportType,
}: Props) {
  const { logEvent } = useTracking();
  const { openPaywallModal } = usePaywall();
  const navigate = useNavigate();
  const subscription = useSubscription();
  const { data: currentUser } = useCurrentUser();
  const [isAskAiModalOpen, setIsAskAiModalOpen] = useState(false);

  const { viewRecord } = useRestricedViewNotice("Title click");

  const handleNoticeLink = (noticeId: string) => {
    if (currentUser) {
      viewRecord(noticeId);
    } else {
      window.open(`${routes.notices}/${noticeId}`, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      <NoticeHeader>
        <TitleContent>
          <Text h1>{title}</Text>
          {!!currentUser && (
            <Flex gap={8} alignItems="center">
              {hasEditAccess && (
                <Button
                  icon="settings01"
                  buttonType="secondary"
                  onClick={async () => {
                    await navigate({
                      to:
                        reportType === BidReportType.RequirementSummary
                          ? settingsRoutes.bidSelfServe
                          : settingsRoutes.qualificationReport,
                    });
                  }}
                >
                  Configure report
                </Button>
              )}

              {showAskAI && (
                <Button
                  onClick={() => {
                    if (!subscription.hasDataTypes("BIDS")) {
                      return openPaywallModal("BID_STOTLES_AI");
                    }
                    logEvent(EventNames.stotlesAiOpened, {
                      "Bid report ID": reportId,
                      "Record GUID": noticeId,
                      "Bid ID": bidId,
                      "Report type": reportType,
                    });
                    setIsAskAiModalOpen(true);
                  }}
                  iconProps={{ size: 16 }}
                  icon="magicWand"
                >
                  Ask Stotles AI
                </Button>
              )}
            </Flex>
          )}
        </TitleContent>
        <Properties>
          {bidName && !!currentUser && (
            <Property>
              <PropTitle>
                <IconWrapper icon="feather" color="sysTextSecondary" />
                Bid
              </PropTitle>
              <ClassNames>
                {({ css, theme }) => (
                  <EllipsisTooltipText
                    fullText={bidName}
                    containerClassname={css({
                      fontWeight: 500,
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      maxWidth: 300,
                      color: theme.colors.sysPrimaryDefault,
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#3160c4",
                      },
                    })}
                    textProps={{ onClick: () => navigate({ to: `${routes.bids}/${bidId}` }) }}
                  />
                )}
              </ClassNames>
            </Property>
          )}
          {noticeId && noticeTitle && (
            <Property>
              <PropTitle>
                <IconWrapper icon="file06" color="sysTextSecondary" />
                Notice title
              </PropTitle>
              <ClassNames>
                {({ css, theme }) => (
                  <EllipsisTooltipText
                    fullText={noticeTitle}
                    containerClassname={css({
                      fontWeight: 500,
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      maxWidth: 300,
                      color: theme.colors.sysPrimaryDefault,
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#3160c4",
                      },
                    })}
                    textProps={{ onClick: () => handleNoticeLink(noticeId) }}
                  />
                )}
              </ClassNames>
            </Property>
          )}
          {buyerName && buyerId && (
            <Property>
              <PropTitle>
                <IconWrapper color="sysTextSecondary" icon="wallet02" />
                Buyer
              </PropTitle>
              <ClassNames>
                {({ css, theme }) => (
                  <EllipsisTooltipText
                    fullText={buyerName}
                    containerClassname={css({
                      fontWeight: 500,
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      maxWidth: 300,
                      color: theme.colors.sysPrimaryDefault,
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#3160c4",
                      },
                    })}
                    textProps={{
                      onClick: () =>
                        window.open(`${routes.buyer}/${buyerId}`, "_blank", "noopener,noreferrer"),
                    }}
                  />
                )}
              </ClassNames>
            </Property>
          )}
        </Properties>
      </NoticeHeader>
      {showAskAI && (
        <AskStotlesAiModal
          resourceId={bidId}
          resourceName={bidName ?? ""}
          noticeTitle={noticeTitle}
          isOpen={isAskAiModalOpen}
          onClose={() => setIsAskAiModalOpen(false)}
          bidId={bidId}
        />
      )}
    </>
  );
}

const NoticeHeader = styled(Flex)({
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: "0 16px",
});

const TitleContent = styled(Flex)({
  justifyContent: "space-between",
  width: "100%",
  marginBottom: 32,
});

const IconWrapper = styled(UIcon)({
  marginRight: 8,
});

const Properties = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 8,
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  width: "100%",
  paddingBottom: 26,
}));

const Property = styled.span({
  display: "flex",
});

const PropTitle = styled.span(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  minWidth: 130,
  color: theme.colors.sysTextSecondary,
}));
